/**
 * classifyPipelineZones — splits pipeline render hints into three layout zones.
 *
 * Zones:
 *   headVizs   — leading contiguous visualizer steps (input views)
 *   transforms — everything in the middle (transforms + mid-pipeline visualizers)
 *   tailVizs   — trailing contiguous visualizer steps (output views)
 *
 * @see FR-VIZ-1b, FR-VIZ-1c
 */

import type { StepRenderHint } from '@/hooks/useVisualizerStepDetection';

export interface PipelineZones {
  headVizs: StepRenderHint[];
  transforms: StepRenderHint[];
  tailVizs: StepRenderHint[];
}

export function classifyPipelineZones(hints: StepRenderHint[]): PipelineZones {
  const head: StepRenderHint[] = [];
  const transforms: StepRenderHint[] = [];
  const tail: StepRenderHint[] = [];

  // First pass: place tagged visualizers by their panelRole
  const untaggedVizs: { hint: StepRenderHint; idx: number }[] = [];
  
  for (let i = 0; i < hints.length; i++) {
    const h = hints[i];
    if (!h.isVisualizer) {
      transforms.push(h);
    } else if (h.step.panelRole === 'input') {
      head.push(h);
    } else if (h.step.panelRole === 'output') {
      tail.push(h);
    } else {
      untaggedVizs.push({ hint: h, idx: i });
    }
  }

  // Second pass: untagged vizs go by position relative to transforms
  if (transforms.length > 0) {
    const firstTransformIdx = hints.findIndex(h => !h.isVisualizer);
    const lastTransformIdx = hints.length - 1 - [...hints].reverse().findIndex(h => !h.isVisualizer);
    
    for (const { hint, idx } of untaggedVizs) {
      if (idx < firstTransformIdx) head.push(hint);
      else if (idx > lastTransformIdx) tail.push(hint);
      else transforms.push(hint); // mid-pipeline viz stays with transforms
    }
  } else {
    // All visualizers, no transforms — split untagged evenly
    const splitPoint = Math.ceil(untaggedVizs.length / 2);
    for (let i = 0; i < untaggedVizs.length; i++) {
      if (i < splitPoint) head.push(untaggedVizs[i].hint);
      else tail.push(untaggedVizs[i].hint);
    }
  }

  return { headVizs: head, transforms, tailVizs: tail };
}
